import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSuppliers, type Supplier } from '@/services/suppliers';
import { updatePurchase, type Purchase } from '@/services/purchases';

interface PurchaseEditModalProps {
  purchase: Purchase;
  onClose: () => void;
  onSaved: () => void;
}

export default function PurchaseEditModal({ purchase, onClose, onSaved }: PurchaseEditModalProps) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState(purchase.supplierId);
  const [invoiceNumber, setInvoiceNumber] = useState(purchase.invoiceNumber);
  const [notes, setNotes] = useState(purchase.notes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSuppliers().then((r) => setSuppliers(r.data));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);
    const { error: saveError } = await updatePurchase(purchase.id, {
      supplierId,
      invoiceNumber,
      notes,
    });
    setSaving(false);
    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title="Editar compra"
      subtitle="Corrige el número de factura, el proveedor o las notas."
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Proveedor</label>
          <select className={inputCls} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Sin proveedor</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.companyName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Nº de factura</label>
          <input
            className={inputCls}
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="FAC-2026-000000 (déjalo vacío si no tiene factura)"
          />
          <p className="text-xs text-foreground-400 mt-1">
            Si la compra no trae factura, déjalo vacío y seguirá figurando como &quot;Sin factura&quot;.
          </p>
        </div>

        <div>
          <label className={labelCls}>Notas</label>
          <textarea
            className={`${inputCls} resize-none`}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Detalles de la compra..."
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-1.5"></i>Guardar cambios
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}